<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1200px"
    scrollable
    data-testid="tbc-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-compare</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">เทียบไฟล์ export (JSON)</div>
          <div class="text-caption text-white-50">
            วางหรือเลือกไฟล์ที่ export ไว้ 2 ไฟล์ แล้วดูว่าต่างกันตรงไหน (ซ้าย = ไฟล์เก่ากว่า)
          </div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="tbc-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- ── ช่องใส่ไฟล์ 2 ฝั่ง ───────────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2 mb-2">
          <div class="text-subtitle-2 flex-grow-1">
            <v-icon size="small" class="mr-1">mdi-file-compare</v-icon>
            ไฟล์ที่จะเทียบ
          </div>
          <v-btn
            size="small"
            variant="text"
            prepend-icon="mdi-swap-horizontal"
            :disabled="!hasAnyInput"
            data-testid="tbc-swap"
            @click="swapSides"
          >
            สลับซ้าย-ขวา
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            prepend-icon="mdi-broom"
            :disabled="!hasAnyInput"
            data-testid="tbc-clear"
            @click="clearAll"
          >
            ล้างทั้งหมด
          </v-btn>
          <v-btn
            size="small"
            variant="text"
            :prepend-icon="inputsOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            data-testid="tbc-toggle-inputs"
            @click="inputsOpen = !inputsOpen"
          >
            {{ inputsOpen ? 'ย่อช่องใส่ไฟล์' : 'แสดงช่องใส่ไฟล์' }}
          </v-btn>
        </div>

        <v-expand-transition>
          <v-row v-show="inputsOpen" dense>
            <v-col v-for="side in sides" :key="side.name" cols="12" md="6">
              <div
                class="tbc-drop pa-3"
                :class="{ 'tbc-drop--over': dragSide === side.name, 'tbc-drop--error': !!side.state.error }"
                :data-testid="`tbc-drop-${side.name}`"
                @dragover.prevent="dragSide = side.name"
                @dragleave.prevent="onDragLeave(side.name)"
                @drop.prevent="onDrop(side.name, $event)"
              >
                <div class="d-flex align-center flex-wrap ga-2 mb-2">
                  <v-chip size="small" :color="side.color" variant="flat">{{ side.title }}</v-chip>
                  <v-btn
                    size="x-small"
                    variant="outlined"
                    prepend-icon="mdi-file-upload-outline"
                    :data-testid="`tbc-pick-${side.name}`"
                    @click="pickFile(side.name)"
                  >
                    เลือกไฟล์ .json
                  </v-btn>
                  <span
                    v-if="side.state.fileName"
                    class="text-caption text-medium-emphasis text-truncate"
                    style="max-width: 220px"
                    :data-testid="`tbc-filename-${side.name}`"
                  >
                    {{ side.state.fileName }}
                  </span>
                  <v-spacer />
                  <span class="text-caption text-medium-emphasis">หรือลากไฟล์มาวางที่นี่</span>
                </div>

                <v-textarea
                  :model-value="side.state.text"
                  :label="`วาง JSON ฝั่ง${side.title}`"
                  rows="4"
                  variant="outlined"
                  density="compact"
                  hide-details
                  :data-testid="`tbc-text-${side.name}`"
                  @update:model-value="onTextInput(side.name, $event)"
                />

                <v-alert
                  v-if="side.state.error"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                  :data-testid="`tbc-error-${side.name}`"
                >
                  {{ side.state.error }}
                </v-alert>

                <v-alert
                  v-else-if="side.state.header"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                  icon="mdi-file-document-outline"
                  :data-testid="`tbc-header-${side.name}`"
                >
                  <div class="text-caption">
                    <div class="font-weight-medium">{{ side.state.header.kindLabel }}</div>
                    <div v-for="line in headerLines(side.state.header)" :key="line.label">
                      {{ line.label }}: <strong>{{ line.value }}</strong>
                    </div>
                  </div>
                </v-alert>
              </div>
            </v-col>
          </v-row>
        </v-expand-transition>

        <input
          ref="fileLeft"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="tbc-file-left"
          @change="onFileChosen('left', $event)"
        >
        <input
          ref="fileRight"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="tbc-file-right"
          @change="onFileChosen('right', $event)"
        >

        <!-- ── ผลการเทียบ ───────────────────────────────────────────── -->
        <v-alert
          v-if="compareError"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mt-4"
          data-testid="tbc-compare-error"
        >
          {{ compareError }}
        </v-alert>

        <p
          v-else-if="!diff"
          class="text-caption text-medium-emphasis mt-4 mb-0"
          data-testid="tbc-idle"
        >
          ใส่ไฟล์ให้ครบทั้ง 2 ฝั่ง แล้วผลเทียบจะขึ้นตรงนี้ทันที — รองรับไฟล์ชุดคำถาม
          (<code>lhb.taxquestion.v2.bundle</code>) และชุดสูตรคำนวณ (<code>lhb.taxcalc.v2.bundle</code>)
        </p>

        <template v-else>
          <v-alert
            v-if="swapHint"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-4"
            data-testid="tbc-swap-hint"
          >
            ไฟล์ฝั่งขวาถูก export ก่อนฝั่งซ้าย — กด «สลับซ้าย-ขวา» ถ้าอยากอ่านจากเก่าไปใหม่
          </v-alert>

          <v-divider class="my-4" />

          <div class="d-flex flex-wrap align-center ga-2 mb-4" data-testid="tbc-summary">
            <v-chip color="success" variant="tonal" size="small" prepend-icon="mdi-plus">
              เพิ่ม {{ diff.summary.added }}
            </v-chip>
            <v-chip color="error" variant="tonal" size="small" prepend-icon="mdi-minus">
              ลบ {{ diff.summary.removed }}
            </v-chip>
            <v-chip color="primary" variant="tonal" size="small" prepend-icon="mdi-pencil">
              แก้ {{ diff.summary.modified }}
            </v-chip>
            <v-chip variant="tonal" size="small" prepend-icon="mdi-equal">
              เหมือนเดิม {{ diff.summary.unchanged }}
            </v-chip>
            <v-spacer />
            <v-btn-toggle
              v-model="changedOnly"
              color="primary"
              density="comfortable"
              variant="outlined"
              mandatory
              data-testid="tbc-filter"
            >
              <v-btn :value="true" size="small">แสดงเฉพาะที่เปลี่ยน</v-btn>
              <v-btn :value="false" size="small">แสดงทั้งหมด</v-btn>
            </v-btn-toggle>
          </div>

          <div v-for="group in visibleGroups" :key="group.name" class="mb-5">
            <div class="text-subtitle-2 font-weight-medium mb-2">
              {{ group.title }}
              <span class="text-caption text-medium-emphasis">({{ group.rows.length }})</span>
            </div>

            <p v-if="!group.rows.length" class="text-caption text-medium-emphasis mb-0">
              {{ changedOnly ? 'ไม่มีการเปลี่ยนแปลงในกลุ่มนี้' : 'ไม่มีข้อมูลในกลุ่มนี้' }}
            </p>

            <v-expansion-panels v-else variant="accordion" multiple :data-testid="group.testid">
              <v-expansion-panel v-for="row in group.rows" :key="row.key">
                <v-expansion-panel-title>
                  <div class="d-flex align-center flex-wrap ga-2 w-100">
                    <v-icon :color="stateColor(row.state)" size="18">{{ stateIcon(row.state) }}</v-icon>
                    <span class="font-weight-medium">{{ row.label || '(ไม่มีชื่อ)' }}</span>
                    <v-chip v-for="chip in row.chips" :key="chip" size="x-small" variant="tonal">
                      {{ chip }}
                    </v-chip>
                    <v-spacer />
                    <v-chip size="x-small" variant="tonal" :color="stateColor(row.state)">
                      {{ stateLabel(row.state) }}
                      <template v-if="row.fields.length"> · {{ row.fields.length }} ฟิลด์</template>
                    </v-chip>
                  </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <p v-if="!row.fields.length" class="text-caption text-medium-emphasis mb-0">
                    ไม่มีการเปลี่ยนแปลง
                  </p>
                  <v-table v-else density="compact" class="tbc-table">
                    <thead>
                      <tr>
                        <th class="text-left" style="width: 220px">ฟิลด์</th>
                        <th class="text-left">{{ leftLabel }}</th>
                        <th class="text-left">{{ rightLabel }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in row.fields" :key="row.key + '|' + f.name">
                        <td class="font-weight-medium">{{ f.name }}</td>
                        <td class="tbc-value">{{ showValue(f.left) }}</td>
                        <td class="tbc-value">{{ showValue(f.right) }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="outlined" color="primary" data-testid="tbc-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * เทียบไฟล์ export ของ Tax Planning V2 สองไฟล์ — ฟีเจอร์อิสระบนหน้า Tax Configuration V2
 * ที่ไม่ผูกกับปีภาษี / เวอร์ชัน / ฐานข้อมูลใด ๆ เลย.
 *
 * ต่างจาก `TaxQuestionHistoryCompareV2Dialog` ตรงที่ตัวนั้นเทียบ "2 รอบบันทึกของเวอร์ชันเดียวกัน"
 * โดยยิง API ไปดึง snapshot มา — ที่นี่ข้อมูลทั้งหมดมาจากไฟล์ที่ผู้ใช้ถืออยู่ จึงเทียบข้ามเครื่อง
 * (Local ↔ Dev ↔ UAT ↔ PRD) ได้ ซึ่งเป็นเคสที่ snapshot ทำไม่ได้เพราะแต่ละเครื่องมีประวัติของตัวเอง.
 *
 * ทั้งการ parse และการ diff อยู่ใน `utils/taxBundleCompare.js` (pure, มีเทสต์) — ไฟล์นี้เหลือแค่
 * การรับ input (วาง / เลือกไฟล์ / ลากวาง) และการเรนเดอร์.
 *
 * ผลเทียบเป็น computed: พอทั้งสองฝั่ง parse ผ่าน ผลก็ขึ้นเอง ไม่มีปุ่ม «เทียบ» ให้กดค้าง และการแก้
 * ข้อความฝั่งไหนก็ตามทำให้ผลอัปเดตทันที (ไม่มี state ค้างที่อธิบายไฟล์เวอร์ชันก่อนหน้า).
 */
import {
  parseBundleText,
  diffBundles,
  rightLooksOlder,
} from '@/utils/taxBundleCompare';
import { truncateValue } from '@/utils/taxQuestionSnapshotDiff';

const STATE_ICON = {
  added: 'mdi-plus',
  removed: 'mdi-minus',
  modified: 'mdi-pencil',
  unchanged: 'mdi-equal',
};
const STATE_COLOR = {
  added: 'success',
  removed: 'error',
  modified: 'primary',
  unchanged: 'grey',
};
const STATE_LABEL = {
  added: 'เพิ่ม',
  removed: 'ลบ',
  modified: 'แก้',
  unchanged: 'เหมือนเดิม',
};

const emptySide = () => ({ text: '', fileName: '', error: '', header: null, parsed: null });

export default {
  name: 'TaxBundleCompareV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      left: emptySide(),
      right: emptySide(),
      changedOnly: true,
      inputsOpen: true,
      /** ฝั่งที่กำลังมีไฟล์ลอยอยู่เหนือ dropzone — ใช้ไฮไลต์กรอบเท่านั้น */
      dragSide: '',
    };
  },
  computed: {
    sides() {
      return [
        { name: 'left', title: 'ซ้าย (เก่ากว่า)', color: 'grey-darken-1', state: this.left },
        { name: 'right', title: 'ขวา (ใหม่กว่า)', color: 'primary', state: this.right },
      ];
    },
    hasAnyInput() {
      return !!(this.left.text || this.right.text);
    },
    /** ผลเทียบ — คำนวณใหม่ทุกครั้งที่ฝั่งใดฝั่งหนึ่งเปลี่ยน */
    result() {
      if (!this.left.parsed || !this.right.parsed) return null;
      return diffBundles(this.left.parsed, this.right.parsed);
    },
    diff() {
      return this.result && this.result.ok ? this.result : null;
    },
    /** เทียบไม่ได้ทั้งที่ทั้งสองฝั่งอ่านออก (คนละ kind) — คนละเรื่องกับ error ของแต่ละฝั่ง */
    compareError() {
      return this.result && !this.result.ok ? this.result.error : '';
    },
    visibleGroups() {
      if (!this.diff) return [];
      return this.diff.groups.map((g) => ({ ...g, rows: this.visible(g.rows) }));
    },
    leftLabel() {
      return (this.left.header && this.left.header.label) || 'ซ้าย';
    },
    rightLabel() {
      return (this.right.header && this.right.header.label) || 'ขวา';
    },
    swapHint() {
      return rightLooksOlder(this.left.header, this.right.header);
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.inputsOpen = true;
    },
    /** พอเทียบได้ครั้งแรก ย่อช่องใส่ไฟล์ให้เอง เพื่อให้ผลเทียบได้พื้นที่เต็ม */
    diff(now, before) {
      if (now && !before) this.inputsOpen = false;
    },
  },
  methods: {
    stateIcon(state) {
      return STATE_ICON[state] || 'mdi-help';
    },
    stateColor(state) {
      return STATE_COLOR[state] || 'grey';
    },
    stateLabel(state) {
      return STATE_LABEL[state] || state;
    },
    /** ค่าว่างฝั่งใดฝั่งหนึ่งแปลว่า «ฝั่งนี้ไม่มีค่า» — บอกด้วยขีด ไม่ใช่ปล่อยช่องโล่ง */
    showValue(v) {
      const s = truncateValue(v);
      return s === '' ? '—' : s;
    },
    visible(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return this.changedOnly ? list.filter((r) => r.state !== 'unchanged') : list;
    },
    headerLines(header) {
      const lines = [];
      if (header.versionCode) lines.push({ label: 'รหัสชุด', value: header.versionCode });
      if (header.taxYearCode || header.beYear) {
        lines.push({ label: 'ปีภาษี', value: [header.taxYearCode, header.beYear].filter(Boolean).join(' · ') });
      }
      if (header.configVersionCode) lines.push({ label: 'คอนฟิก', value: header.configVersionCode });
      if (header.exportedAt) lines.push({ label: 'export เมื่อ', value: header.exportedAt });
      if (header.exportedBy) lines.push({ label: 'export โดย', value: header.exportedBy });
      header.counts.forEach((c) => lines.push({ label: c.label, value: String(c.value) }));
      return lines;
    },
    /** อ่านข้อความของฝั่งนั้นใหม่ทั้งหมด — ทางเดียวที่ state ของฝั่งจะเปลี่ยน */
    reparse(side) {
      const s = this[side];
      if (!s.text.trim()) {
        s.error = '';
        s.header = null;
        s.parsed = null;
        return;
      }
      const parsed = parseBundleText(s.text, { fileName: s.fileName });
      if (parsed.ok) {
        s.error = '';
        s.header = parsed.header;
        s.parsed = parsed;
      } else {
        s.error = parsed.error;
        s.header = null;
        s.parsed = null;
      }
    },
    onTextInput(side, value) {
      this[side].text = value || '';
      // พิมพ์/วางเองแล้ว ชื่อไฟล์เดิมไม่ได้อธิบายข้อความที่อยู่ตรงนั้นอีกต่อไป
      this[side].fileName = '';
      this.reparse(side);
    },
    pickFile(side) {
      const el = this.$refs[side === 'left' ? 'fileLeft' : 'fileRight'];
      // ล้างก่อน เพื่อให้เลือกไฟล์เดิมซ้ำแล้ว `change` ยังยิง
      if (el) { el.value = ''; el.click(); }
    },
    onFileChosen(side, evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (file) this.readFile(side, file);
    },
    onDragLeave(side) {
      if (this.dragSide === side) this.dragSide = '';
    },
    onDrop(side, evt) {
      this.dragSide = '';
      const file = evt && evt.dataTransfer && evt.dataTransfer.files && evt.dataTransfer.files[0];
      if (file) this.readFile(side, file);
    },
    readFile(side, file) {
      const s = this[side];
      s.fileName = file.name || '';
      const reader = new FileReader();
      reader.onload = () => {
        s.text = String(reader.result || '');
        this.reparse(side);
      };
      reader.onerror = () => {
        s.text = '';
        s.header = null;
        s.parsed = null;
        s.error = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    swapSides() {
      const l = this.left;
      this.left = this.right;
      this.right = l;
    },
    clearAll() {
      this.left = emptySide();
      this.right = emptySide();
      this.inputsOpen = true;
      ['fileLeft', 'fileRight'].forEach((ref) => {
        const el = this.$refs[ref];
        if (el) el.value = '';
      });
    },
    close() {
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.tbc-drop {
  border: 1px dashed rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.tbc-drop--over {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.06);
}
.tbc-drop--error {
  border-color: rgb(var(--v-theme-error));
}
.tbc-table :deep(td),
.tbc-table :deep(th) {
  vertical-align: top;
}
.tbc-value {
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 380px;
}
</style>
